import { apiRequest } from './client'
import { SILKREELS_API_BASE, TENANT_HEADER } from './config'
import { getActiveTenantCode } from '@/shared/lib/tenant-code'
import { ensureFreshAccessToken, readAccessToken } from './authSession'
import { resolveApiLocale } from './client'
import type { MemberRole, ProjectType, StorageMode } from '@/features/studio/lib/production-constants'

function normalizeListItems<T>(items: T[] | null | undefined): T[] {
  return items ?? []
}

export type ProductionPhase =
  | 'development'
  | 'pre_production'
  | 'production'
  | 'post'
  | 'delivery'

export interface Production {
  id: number
  public_id: string
  title: string
  description: string
  series_outline?: string
  phase?: ProductionPhase
  aspect_ratio: string
  episode_target: number
  episodes_per_season?: number
  work_id?: number | null
  role?: MemberRole | string
  member_count?: number
  project_type?: ProjectType | string
  default_locale?: string
  script_convention?: 'cn' | 'hollywood' | string
  storage_mode?: StorageMode | string
  is_owner?: boolean
  user_id?: number
  created_at: string
  updated_at: string
}

export interface ProductionMember {
  user_id: number
  role: MemberRole | string
  display_name?: string
  username?: string
  department_id?: string | null
  department_name?: string
  is_department_admin?: boolean
  created_at: string
}

export interface CollaborationEligibility {
  can_invite: boolean
  has_studio_collaboration: boolean
  collaboration_seats: number
  collaboration_seats_used: number
  seats_remaining: number
  members_count: number
}

export interface StudioUserSearchHit {
  user_id: number
  username: string
  display_name?: string
}

export interface ProductionInvite {
  token: string
  invite_url: string
  email?: string
  role: string
  department_id?: string
  expires_at: string
  email_sent?: boolean
}

export interface ProductionPendingInvite {
  id: number
  token: string
  invite_url: string
  email?: string
  role: string
  department_id?: string
  department_name?: string
  status: string
  expires_at: string
  created_at: string
}

export interface ProductionInvitePreview {
  production_public_id: string
  production_title: string
  role: string
  department_name?: string
  status: string
  expires_at: string
}

export interface ProductionDepartment {
  id: string
  name: string
  description?: string
  member_count?: number
  sort_order?: number
}

export interface ProductionAsset {
  id: string
  public_id?: string
  name: string
  kind: string
  status: string
  registered?: boolean
  url?: string
  media_url?: string
  mime_type?: string
  source?: string
  tags?: string[]
  note?: string
  created_at?: string
  canvas_node_id?: string
}

export interface ProductionList {
  items: Production[]
  total: number
  limit: number
  offset: number
}

export interface ScriptScene {
  scene_no: number
  heading: string
  body: string
  storyboard_url?: string
}

export interface Script {
  script_id: number
  episode_no: number
  title?: string
  format: string
  body: string
  outline?: string
  status: 'draft' | 'finalized' | string
  current_revision: number
  locked_revision?: number | null
  default_locale?: string
  script_convention?: 'cn' | 'hollywood' | string
  translations?: Record<string, Record<string, string>>
  scenes?: ScriptScene[]
  updated_at: string
  finalized_at?: string | null
}

export interface ScriptBreakdownResult {
  locked_revision: number
  scenes: ScriptScene[]
  shots_created: number
  shots_skipped: number
}

export interface ScriptEpisodeSummary {
  episode_no: number
  title?: string
  season_no: number
  episode_in_season: number
  status: 'draft' | 'finalized' | string
  current_revision: number
  locked_revision?: number | null
  scene_count: number
  has_body: boolean
  has_outline?: boolean
  updated_at?: string | null
}

export interface ScriptEpisodeList {
  episode_target: number
  episodes_per_season: number
  items: ScriptEpisodeSummary[]
}

export interface Shot {
  id: number
  public_id: string
  script_id: number
  episode_no: number
  scene_no: number
  shot_no: number
  slug: string
  description: string
  shot_size: string
  camera_angle: string
  camera_movement: string
  dialogue: string
  sound_note: string
  music_note: string
  storyboard_note: string
  status: string
  duration_sec: number
  created_at: string
  updated_at: string
}

export interface ShotList {
  items: Shot[]
  total: number
  limit: number
  offset: number
}

export interface ShotTake {
  id: number
  version_no: number
  media_url: string
  media_kind: string
  ai_task_id?: string | null
  prompt: string
  model_id: string
  status: string
  is_preferred: boolean
  created_at: string
}

export interface ShotTakeList {
  items: ShotTake[]
}

export function listProductions(limit = 20, offset = 0): Promise<ProductionList> {
  return apiRequest<ProductionList>(`/api/v1/studio/productions?limit=${limit}&offset=${offset}`)
}

export function createProduction(body: {
  title: string
  description?: string
  aspect_ratio?: string
  episode_target?: number
  episodes_per_season?: number
  project_type?: ProjectType | string
  default_locale?: string
  storage_mode?: StorageMode | string
  creator_role?: MemberRole | string
  storage_config?: Record<string, string>
}): Promise<Production> {
  return apiRequest<Production>('/api/v1/studio/productions', { method: 'POST', body })
}

export function patchProduction(
  publicId: string,
  body: Partial<{
    title: string
    description: string
    series_outline?: string
    aspect_ratio: string
    episode_target: number
    episodes_per_season?: number
    project_type: string
    default_locale: string
    script_convention?: 'cn' | 'hollywood' | string
  }>,
): Promise<Production> {
  return apiRequest<Production>(`/api/v1/studio/productions/${encodeURIComponent(publicId)}`, {
    method: 'PATCH',
    body,
  })
}

export function getProduction(publicId: string): Promise<Production> {
  return apiRequest<Production>(`/api/v1/studio/productions/${encodeURIComponent(publicId)}`)
}

export interface StudioCanvasGraph {
  nodes: unknown[]
  edges: unknown[]
  viewport: { x: number; y: number; zoom: number }
  revision?: number
  scope_type?: string
  scope_key?: string
}

export interface CanvasGraphScope {
  scopeType: string
  scopeKey: string
}

function canvasGraphQuery(scope?: CanvasGraphScope): string {
  if (!scope) return ''
  const q = new URLSearchParams()
  q.set('scope_type', scope.scopeType)
  q.set('scope_key', scope.scopeKey)
  const s = q.toString()
  return s ? `?${s}` : ''
}

export function getCanvasGraph(publicId: string, scope?: CanvasGraphScope): Promise<StudioCanvasGraph> {
  return apiRequest<StudioCanvasGraph>(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/canvas/graph${canvasGraphQuery(scope)}`,
  )
}

export function saveCanvasGraph(
  publicId: string,
  body: {
    nodes: unknown[]
    edges: unknown[]
    viewport: { x: number; y: number; zoom: number }
    if_match_revision?: number
  },
  scope?: CanvasGraphScope,
): Promise<StudioCanvasGraph> {
  return apiRequest<StudioCanvasGraph>(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/canvas/graph${canvasGraphQuery(scope)}`,
    { method: 'PUT', body },
  )
}

export interface CanvasBoard {
  id: string
  title: string
  scope_type: string
  scope_key: string
  is_default: boolean
  updated_at: string
}

export function listCanvasBoards(publicId: string): Promise<{ items: CanvasBoard[] }> {
  return apiRequest<{ items: CanvasBoard[] }>(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/canvas/boards`,
  )
}

export function createCanvasBoard(publicId: string, title: string): Promise<CanvasBoard> {
  return apiRequest<CanvasBoard>(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/canvas/boards`, {
    method: 'POST',
    body: { title },
  })
}

export function patchCanvasBoard(publicId: string, boardKey: string, title: string): Promise<CanvasBoard> {
  return apiRequest<CanvasBoard>(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/canvas/boards/${encodeURIComponent(boardKey)}`,
    { method: 'PATCH', body: { title } },
  )
}

export function deleteCanvasBoard(publicId: string, boardKey: string): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/canvas/boards/${encodeURIComponent(boardKey)}`,
    { method: 'DELETE' },
  )
}

export type OverviewTodoType = 'task' | 'review' | 'comment' | 'bounty'

export interface OverviewTodo {
  type: OverviewTodoType
  title: string
  hint?: string
  link_path: string
  ref_id?: string
}

export interface OverviewEpisodeProgress {
  episode_no: number
  episode_label: string
  total: number
  approved: number
  storyboarded: number
  with_take: number
}

export interface OverviewMemberPreview {
  user_id: number
  display_name?: string
  username?: string
  role: string
}

export interface ProductionOverview {
  todos: OverviewTodo[]
  progress: OverviewEpisodeProgress[]
  member_preview: OverviewMemberPreview[]
  open_comments: number
  shot_count: number
}

export function getProductionOverview(publicId: string): Promise<ProductionOverview> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/overview`)
}

export function deleteProduction(publicId: string): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(`/api/v1/studio/productions/${encodeURIComponent(publicId)}`, {
    method: 'DELETE',
  })
}

export function listMembers(
  publicId: string,
  opts?: { limit?: number; offset?: number },
): Promise<{ items: ProductionMember[]; total: number; limit: number; offset: number }> {
  const params = new URLSearchParams()
  if (opts?.limit != null) params.set('limit', String(opts.limit))
  if (opts?.offset != null) params.set('offset', String(opts.offset))
  const q = params.toString()
  return apiRequest<{ items?: ProductionMember[] | null; total?: number; limit?: number; offset?: number }>(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/members${q ? `?${q}` : ''}`,
  ).then((res) => ({
    items: res.items ?? [],
    total: res.total ?? (res.items ?? []).length,
    limit: res.limit ?? opts?.limit ?? 20,
    offset: res.offset ?? opts?.offset ?? 0,
  }))
}

export function addMember(
  publicId: string,
  body: { user_id: number; role: MemberRole | string; department_id?: string },
): Promise<{ ok: boolean }> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/members`, {
    method: 'POST',
    body,
  })
}

export function removeMember(publicId: string, userId: number): Promise<{ ok: boolean }> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/members/${userId}`,
    { method: 'DELETE' },
  )
}

export function patchMember(
  publicId: string,
  userId: number,
  body: {
    role?: MemberRole | string
    department_id?: string | null
    is_department_admin?: boolean
  },
): Promise<{ ok: boolean }> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/members/${userId}`,
    { method: 'PATCH', body },
  )
}

export interface CollaborationTask {
  id: number
  title: string
  description: string
  status: string
  reward_points: number
  slots_total: number
  slots_taken: number
  slots_left: number
  visibility: string
  auto_join_production: boolean
  join_member_role: string
  published_at?: string
  deadline?: string
  created_at: string
}

export interface CollaborationTaskAssignment {
  id: number
  task_id: number
  user_id: number
  status: string
  deliverable_note?: string
  review_feedback?: string
  created_at: string
  updated_at: string
}

export function listCollaborationTasks(
  publicId: string,
  limit = 50,
): Promise<{ items: CollaborationTask[] }> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/collaboration-tasks?limit=${limit}`,
  )
}

export function createCollaborationTask(
  publicId: string,
  body: {
    title: string
    description?: string
    reward_points: number
    slots_total?: number
    visibility?: string
    auto_join_production?: boolean
    join_member_role?: string
    join_department_id?: string
  },
): Promise<CollaborationTask> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/collaboration-tasks`, {
    method: 'POST',
    body,
  })
}

export function publishCollaborationTask(
  publicId: string,
  taskId: number,
): Promise<CollaborationTask> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/collaboration-tasks/${taskId}/publish`,
    { method: 'POST', body: {} },
  )
}

export function listCollaborationTaskAssignments(
  publicId: string,
  taskId: number,
): Promise<{ items: CollaborationTaskAssignment[] }> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/collaboration-tasks/${taskId}/assignments`,
  )
}

export function approveCollaborationAssignment(
  publicId: string,
  taskId: number,
  assignmentId: number,
  feedback?: string,
): Promise<CollaborationTaskAssignment> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/collaboration-tasks/${taskId}/assignments/${assignmentId}/approve`,
    { method: 'POST', body: feedback ? { feedback } : {} },
  )
}

export function getCollaborationEligibility(publicId: string): Promise<CollaborationEligibility> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/collaboration/eligibility`,
  )
}

export function searchStudioUsers(
  publicId: string,
  q: string,
  limit = 20,
): Promise<{ items: StudioUserSearchHit[] }> {
  const params = new URLSearchParams({ q, limit: String(limit) })
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/users/search?${params.toString()}`,
  )
}

export function createInviteLink(
  publicId: string,
  body: { role: MemberRole | string; department_id?: string; expires_in_hours?: number },
): Promise<ProductionInvite> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/members/invite-link`, {
    method: 'POST',
    body,
  })
}

export function createInviteEmail(
  publicId: string,
  body: { email: string; role: MemberRole | string; department_id?: string; expires_in_hours?: number },
): Promise<ProductionInvite> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/members/invite-email`, {
    method: 'POST',
    body,
  })
}

export function getProductionInvitePreview(token: string): Promise<ProductionInvitePreview> {
  return apiRequest(`/api/v1/studio/productions/invites/${encodeURIComponent(token)}`, { auth: false })
}

export function acceptProductionInvite(token: string): Promise<ProductionMember> {
  return apiRequest(`/api/v1/studio/productions/invites/${encodeURIComponent(token)}/accept`, {
    method: 'POST',
    body: {},
  })
}

export function listProductionInvites(
  publicId: string,
  params?: { status?: string; limit?: number },
): Promise<{ items: ProductionPendingInvite[] }> {
  const q = new URLSearchParams()
  if (params?.status) q.set('status', params.status)
  if (params?.limit) q.set('limit', String(params.limit))
  const suffix = q.toString() ? `?${q.toString()}` : ''
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/members/invites${suffix}`)
}

export function revokeProductionInvite(publicId: string, inviteId: number): Promise<{ ok: boolean }> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/members/invites/${inviteId}`,
    { method: 'DELETE' },
  )
}

export interface ProductionStorageView {
  storage_mode: string
  storage_prefix: string
  storage_config?: Record<string, string>
  asset_count: number
  used_bytes: number
  graph_revision: number
  can_migrate: boolean
  migrate_targets?: string[]
}

export interface StorageMigratePreview {
  from_mode: string
  to_mode: string
  asset_count: number
  used_bytes: number
  copiable_assets: number
}

export interface StorageMigrateResult {
  storage_mode: string
  storage_prefix: string
  migrated_assets: number
  skipped_assets: number
}

export function getProductionStorage(publicId: string): Promise<ProductionStorageView> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/storage`)
}

export function patchProductionStorage(
  publicId: string,
  body: { storage_config: Record<string, string> },
): Promise<ProductionStorageView> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/storage`, {
    method: 'PATCH',
    body,
  })
}

export function previewProductionStorageMigrate(
  publicId: string,
  targetMode: string,
): Promise<StorageMigratePreview> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/storage/migrate/preview?target_mode=${encodeURIComponent(targetMode)}`,
  )
}

export function migrateProductionStorage(
  publicId: string,
  body: { target_mode: string; storage_config?: Record<string, string> },
): Promise<StorageMigrateResult> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/storage/migrate`, {
    method: 'POST',
    body,
  })
}

export function listDepartments(publicId: string): Promise<{ items: ProductionDepartment[] }> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/departments`)
}

export function createDepartment(
  publicId: string,
  body: { name: string; description?: string; sort_order?: number },
): Promise<ProductionDepartment> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/departments`, {
    method: 'POST',
    body,
  })
}

export function patchDepartment(
  publicId: string,
  deptId: string,
  body: { name?: string; description?: string; sort_order?: number },
): Promise<ProductionDepartment> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/departments/${encodeURIComponent(deptId)}`,
    { method: 'PATCH', body },
  )
}

export function deleteDepartment(publicId: string, deptId: string): Promise<{ ok: boolean }> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/departments/${encodeURIComponent(deptId)}`,
    { method: 'DELETE' },
  )
}

export function listAssets(
  publicId: string,
  params?: { kind?: string; status?: string; q?: string; tag?: string },
): Promise<{ items: ProductionAsset[] }> {
  const q = new URLSearchParams()
  if (params?.kind) q.set('kind', params.kind)
  if (params?.status) q.set('status', params.status)
  if (params?.q) q.set('q', params.q)
  if (params?.tag) q.set('tag', params.tag)
  const suffix = q.toString() ? `?${q.toString()}` : ''
  return apiRequest<{ items?: ProductionAsset[] | null }>(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/assets${suffix}`,
  ).then((res) => ({ items: res.items ?? [] }))
}

export function listAssetTags(publicId: string): Promise<{ items: { tag: string; count: number }[] }> {
  return apiRequest<{ items?: { tag: string; count: number }[] | null }>(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/assets/tags`,
  ).then((res) => ({ items: normalizeListItems(res.items) }))
}

export async function uploadProductionAsset(
  publicId: string,
  file: File,
  fields?: {
    name?: string
    note?: string
    kind?: string
    tags?: string[]
    shot_public_id?: string
    bible_character_id?: string
    bible_location_id?: string
    bible_ref_type?: string
    bible_ref_public_id?: string
    shot_scene_key?: string
  },
): Promise<ProductionAsset> {
  const form = new FormData()
  form.append('file', file)
  if (fields?.name) form.append('name', fields.name)
  if (fields?.note) form.append('note', fields.note)
  if (fields?.kind) form.append('kind', fields.kind)
  if (fields?.tags?.length) form.append('tags', JSON.stringify(fields.tags))
  if (fields?.shot_public_id) form.append('shot_public_id', fields.shot_public_id)
  if (fields?.bible_character_id) form.append('bible_character_id', fields.bible_character_id)
  if (fields?.bible_location_id) form.append('bible_location_id', fields.bible_location_id)
  if (fields?.bible_ref_type) form.append('bible_ref_type', fields.bible_ref_type)
  if (fields?.bible_ref_public_id) form.append('bible_ref_public_id', fields.bible_ref_public_id)
  if (fields?.shot_scene_key) form.append('shot_scene_key', fields.shot_scene_key)

  const token = readAccessToken()
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`
  const tenant = getActiveTenantCode()
  if (tenant) headers[TENANT_HEADER] = tenant

  const res = await fetch(
    `${SILKREELS_API_BASE}/api/v1/studio/productions/${encodeURIComponent(publicId)}/assets/upload`,
    { method: 'POST', headers, body: form },
  )
  const text = await res.text()
  let body: { code: number; message?: string; data: ProductionAsset } | null = null
  if (text) {
    body = JSON.parse(text) as { code: number; message?: string; data: ProductionAsset }
  }
  if (!res.ok || !body || body.code !== 0) {
    throw new Error(body?.message ?? res.statusText)
  }
  return body.data
}

export function getProductionAsset(publicId: string, assetId: string): Promise<ProductionAsset> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/assets/${encodeURIComponent(assetId)}`,
  )
}

export function deleteProductionAsset(publicId: string, assetId: string): Promise<{ ok: boolean }> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/assets/${encodeURIComponent(assetId)}`,
    { method: 'DELETE' },
  )
}

export function patchProductionAsset(
  publicId: string,
  assetId: string,
  body: { status?: 'draft' | 'ready' | 'archived'; name?: string; note?: string; tags?: string[] },
): Promise<ProductionAsset> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/assets/${encodeURIComponent(assetId)}`,
    { method: 'PATCH', body },
  )
}

export interface StudioAssetListing {
  listing_public_id: string
  listing_id: number
  status: string
  price_points: number
  license_mode: string
  studio_asset_public_id: string
}

export interface StudioProductionListing {
  listing_public_id: string
  listing_type: string
  title: string
  status: string
  price_points: number
  studio_asset_public_id?: string
  studio_asset_name?: string
  script_episode_no?: number
  script_revision?: number
  created_at: string
}

export interface StudioScriptListing {
  listing_public_id: string
  listing_id: number
  status: string
  price_points: number
  license_mode: string
  episode_no: number
  script_revision: number
}

export function createStudioScriptListing(
  publicId: string,
  scriptId: number,
  episodeNo: number,
  body: {
    title?: string
    summary?: string
    cover_url?: string
    price_points: number
    license_mode?: 'non_exclusive' | 'exclusive'
  },
): Promise<StudioScriptListing> {
  return apiRequest(`${scriptEpisodePath(publicId, scriptId, episodeNo)}/listings`, { method: 'POST', body })
}

export function deleteScriptEpisode(
  publicId: string,
  scriptId: number,
  episodeNo: number,
): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(scriptEpisodePath(publicId, scriptId, episodeNo), { method: 'DELETE' })
}

export function createStudioAssetListing(
  publicId: string,
  assetId: string,
  body: {
    title?: string
    summary?: string
    cover_url?: string
    price_points: number
    license_mode?: 'non_exclusive' | 'exclusive'
  },
): Promise<StudioAssetListing> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/assets/${encodeURIComponent(assetId)}/listings`,
    { method: 'POST', body },
  )
}

export function listProductionListings(
  publicId: string,
  limit = 50,
): Promise<{ items: StudioProductionListing[] }> {
  return apiRequest<{ items?: StudioProductionListing[] | null }>(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/listings?limit=${limit}`,
  ).then((res) => ({ items: normalizeListItems(res.items) }))
}

export function revokeStudioAssetListing(publicId: string, assetId: string): Promise<{ ok: boolean }> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/assets/${encodeURIComponent(assetId)}/listings`,
    { method: 'DELETE' },
  )
}

export function registerProductionAsset(
  publicId: string,
  body: {
    kind?: string
    source?: string
    name?: string
    url: string
    media_kind?: string
    note?: string
    tags?: string[]
    canvas_node_id?: string
    bible_character_id?: string
    bible_location_id?: string
    bible_ref_type?: string
    bible_ref_public_id?: string
    shot_public_id?: string
    shot_scene_key?: string
  },
): Promise<ProductionAsset> {
  return apiRequest<ProductionAsset>(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/assets`, {
    method: 'POST',
    body,
  })
}

export interface ScriptDocument {
  id: number
  title: string
  cover_url?: string
  summary?: string
  series_outline?: string
  story_content?: string
  project_type?: string
  default_locale?: string
  episode_target: number
  episode_count: number
  episodes_per_season: number
  sort_order: number
  status: 'draft' | 'finalized' | string
  current_revision: number
  locked_revision?: number | null
  scene_count: number
  has_body: boolean
  has_outline?: boolean
  updated_at?: string | null
}

export interface ScriptDocumentList {
  items: ScriptDocument[]
  total: number
  limit: number
  offset: number
}

function scriptDocPath(publicId: string, scriptId: number, suffix = '') {
  return `/api/v1/studio/productions/${encodeURIComponent(publicId)}/scripts/${scriptId}${suffix}`
}

function scriptEpisodePath(publicId: string, scriptId: number, episodeNo: number, suffix = '') {
  return `/api/v1/studio/productions/${encodeURIComponent(publicId)}/scripts/${scriptId}/episodes/${episodeNo}${suffix}`
}

export function listScriptDocuments(
  publicId: string,
  opts?: { limit?: number; offset?: number },
): Promise<ScriptDocumentList> {
  const params = new URLSearchParams()
  if (opts?.limit != null) params.set('limit', String(opts.limit))
  if (opts?.offset != null) params.set('offset', String(opts.offset))
  const q = params.toString()
  return apiRequest<ScriptDocumentList & { items?: ScriptDocument[] | null }>(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/scripts${q ? `?${q}` : ''}`,
  ).then((res) => ({
    ...res,
    items: normalizeListItems(res.items),
    total: res.total ?? (res.items ?? []).length,
  }))
}

export function getScriptDocument(publicId: string, scriptId: number): Promise<ScriptDocument> {
  return apiRequest<ScriptDocument>(scriptDocPath(publicId, scriptId))
}

export type WriterImportPart = 'series_outline' | 'story_content' | 'seasons' | 'episodes' | 'scenes'

export interface WriterImportPartPreview {
  part: WriterImportPart
  source: boolean
  imported: boolean
}

export interface WriterImportPreview {
  parts: WriterImportPartPreview[]
  episode_count: number
}

export interface WriterImportResult {
  imported_episode_nos: number[]
  skipped_episode_nos: number[]
  imported_parts?: WriterImportPart[]
}

export interface WriterImportBody {
  writer_script_id: number
  personal_script_id?: number
  episode_nos?: number[]
  overwrite_draft_only?: boolean
  parts?: WriterImportPart[]
}

export function previewWriterImport(
  productionId: string,
  scriptId: number,
  body: { writer_script_id: number; episode_nos?: number[] },
): Promise<WriterImportPreview> {
  return apiRequest(scriptDocPath(productionId, scriptId, '/fork-personal/preview'), { method: 'POST', body })
}

export function attachPersonalScript(
  productionId: string,
  scriptId: number,
  body: WriterImportBody,
): Promise<WriterImportResult> {
  return apiRequest(scriptDocPath(productionId, scriptId, '/attach-personal'), { method: 'POST', body })
}

export function forkPersonalScript(
  productionId: string,
  scriptId: number,
  body: WriterImportBody,
): Promise<WriterImportResult> {
  return apiRequest(scriptDocPath(productionId, scriptId, '/fork-personal'), { method: 'POST', body })
}

/** @deprecated use forkPersonalScript */
export function importFromWriter(
  productionId: string,
  scriptId: number,
  body: WriterImportBody,
): Promise<WriterImportResult> {
  return forkPersonalScript(productionId, scriptId, body)
}

export function createScriptDocument(
  publicId: string,
  body: {
    title?: string
    cover_url?: string
    summary?: string
    project_type?: string
    default_locale?: string
    episode_target?: number
    episodes_per_season?: number
  },
): Promise<ScriptDocument> {
  return apiRequest<ScriptDocument>(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/scripts`, {
    method: 'POST',
    body,
  })
}

export function patchScriptDocument(
  publicId: string,
  scriptId: number,
  body: {
    title?: string
    cover_url?: string
    summary?: string
    series_outline?: string
    story_content?: string
    project_type?: string
    default_locale?: string
    episode_target?: number
    episodes_per_season?: number
  },
): Promise<ScriptDocument> {
  return apiRequest<ScriptDocument>(scriptDocPath(publicId, scriptId), {
    method: 'PATCH',
    body,
  })
}

export function deleteScriptDocument(publicId: string, scriptId: number): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(scriptDocPath(publicId, scriptId), { method: 'DELETE' })
}

export function listScriptEpisodes(
  publicId: string,
  scriptId: number,
): Promise<ScriptEpisodeList> {
  return apiRequest<ScriptEpisodeList>(scriptDocPath(publicId, scriptId, '/episodes'))
}

export function getScript(
  publicId: string,
  scriptId: number,
  episodeNo = 1,
  locale?: string,
): Promise<Script> {
  const q = locale ? `?locale=${encodeURIComponent(locale)}` : ''
  return apiRequest<Script>(`${scriptEpisodePath(publicId, scriptId, episodeNo)}${q}`)
}

export function patchSceneStoryboard(
  publicId: string,
  scriptId: number,
  episodeNo: number,
  sceneNo: number,
  storyboardUrl: string,
): Promise<ScriptScene> {
  return apiRequest<ScriptScene>(
    `${scriptEpisodePath(publicId, scriptId, episodeNo)}/scenes/${sceneNo}/storyboard`,
    { method: 'PATCH', body: { storyboard_url: storyboardUrl } },
  )
}

export function saveScript(
  publicId: string,
  scriptId: number,
  episodeNo: number,
  payload: { title?: string; body?: string; outline?: string; translations?: Record<string, Record<string, string>> },
): Promise<Script> {
  return apiRequest<Script>(scriptEpisodePath(publicId, scriptId, episodeNo), {
    method: 'PUT',
    body: payload,
  })
}

export interface ScriptImportEpisodeItem {
  episode_no: number
  char_count: number
  scene_count: number
  has_existing_body: boolean
  status?: string
  selected: boolean
  skip_reason?: string
  body_preview?: string
}

export interface ScriptImportPreview {
  parsed_count: number
  episode_target: number
  will_extend_target: boolean
  new_episode_target: number
  items: ScriptImportEpisodeItem[]
  warnings?: string[]
}

export interface ScriptImportApplyResult {
  applied: number
  skipped: number
  episode_target: number
  items: ScriptImportEpisodeItem[]
  warnings?: string[]
}

export function previewScriptImport(
  publicId: string,
  scriptId: number,
  body: {
    body: string
    extend_episode_target?: boolean
    episode_nos?: number[]
  },
): Promise<ScriptImportPreview> {
  return apiRequest<ScriptImportPreview>(scriptDocPath(publicId, scriptId, '/import/preview'), {
    method: 'POST',
    body,
  })
}

export function applyScriptImport(
  publicId: string,
  scriptId: number,
  body: {
    body: string
    extend_episode_target?: boolean
    episode_nos?: number[]
  },
): Promise<ScriptImportApplyResult> {
  return apiRequest<ScriptImportApplyResult>(scriptDocPath(publicId, scriptId, '/import/apply'), {
    method: 'POST',
    body,
  })
}

export function parseScript(publicId: string, scriptId: number, episodeNo = 1): Promise<Script> {
  return apiRequest<Script>(`${scriptEpisodePath(publicId, scriptId, episodeNo)}/parse`, { method: 'POST' })
}

export function finalizeScript(
  publicId: string,
  scriptId: number,
  episodeNo = 1,
  changeNote?: string,
): Promise<Script> {
  return apiRequest<Script>(`${scriptEpisodePath(publicId, scriptId, episodeNo)}/finalize`, {
    method: 'POST',
    body: changeNote ? { change_note: changeNote } : {},
  })
}

export interface ScriptRevision {
  revision_no: number
  revision_color: string
  change_note?: string
  created_by: number
  created_at: string
}

export interface ScriptRevisionCompare {
  from_revision: number
  to_revision: number
  from_body: string
  to_body: string
  added_lines: string[]
  removed_lines: string[]
}

export interface ScriptComment {
  public_id: string
  episode_no: number
  scene_no?: number | null
  anchor_start?: number | null
  anchor_end?: number | null
  anchor_quote?: string | null
  author_id: number
  body: string
  resolved: boolean
  created_at: string
}

export function listScriptRevisions(
  publicId: string,
  scriptId: number,
  episodeNo = 1,
): Promise<{ items: ScriptRevision[] }> {
  return apiRequest(`${scriptEpisodePath(publicId, scriptId, episodeNo)}/revisions`)
}

export function compareScriptRevisions(
  publicId: string,
  scriptId: number,
  episodeNo: number,
  from: number,
  to: number,
): Promise<ScriptRevisionCompare> {
  return apiRequest(
    `${scriptEpisodePath(publicId, scriptId, episodeNo)}/revisions/compare?from=${from}&to=${to}`,
  )
}

export function publishScriptRevision(
  publicId: string,
  scriptId: number,
  episodeNo = 1,
  changeNote?: string,
): Promise<Script> {
  return apiRequest(`${scriptEpisodePath(publicId, scriptId, episodeNo)}/revisions/publish`, {
    method: 'POST',
    body: changeNote ? { change_note: changeNote } : {},
  })
}

export function lockScriptRevision(
  publicId: string,
  scriptId: number,
  episodeNo: number,
  revisionNo: number,
): Promise<Script> {
  return apiRequest(`${scriptEpisodePath(publicId, scriptId, episodeNo)}/revisions/lock`, {
    method: 'POST',
    body: { revision_no: revisionNo },
  })
}

export function listScriptComments(
  publicId: string,
  scriptId: number,
  episodeNo = 1,
): Promise<{ items: ScriptComment[]; total?: number }> {
  return apiRequest(`${scriptEpisodePath(publicId, scriptId, episodeNo)}/comments`)
}

export function listScriptDocumentComments(
  publicId: string,
  scriptId: number,
  opts?: { limit?: number; offset?: number },
): Promise<{ items: ScriptComment[]; total: number; limit: number; offset: number }> {
  const params = new URLSearchParams()
  if (opts?.limit != null) params.set('limit', String(opts.limit))
  if (opts?.offset != null) params.set('offset', String(opts.offset))
  const q = params.toString()
  return apiRequest(`${scriptDocPath(publicId, scriptId, '/comments')}${q ? `?${q}` : ''}`)
}

export function createScriptComment(
  publicId: string,
  scriptId: number,
  episodeNo: number,
  body: string,
  opts?: {
    sceneNo?: number
    anchorStart?: number
    anchorEnd?: number
    anchorQuote?: string
  },
): Promise<ScriptComment> {
  return apiRequest(`${scriptEpisodePath(publicId, scriptId, episodeNo)}/comments`, {
    method: 'POST',
    body: {
      body,
      scene_no: opts?.sceneNo,
      anchor_start: opts?.anchorStart,
      anchor_end: opts?.anchorEnd,
      anchor_quote: opts?.anchorQuote,
    },
  })
}

export function resolveScriptComment(
  publicId: string,
  scriptId: number,
  episodeNo: number,
  commentId: string,
  resolved: boolean,
): Promise<ScriptComment> {
  return apiRequest(
    `${scriptEpisodePath(publicId, scriptId, episodeNo)}/comments/${encodeURIComponent(commentId)}`,
    { method: 'PATCH', body: { resolved } },
  )
}

export interface ScriptAIBatchItem {
  episode_no?: number
  field: string
  text?: string
  applied: boolean
  skip_reason?: string
}

export interface ScriptAIBatchResult {
  task: string
  text?: string
  cost?: number
  applied_count: number
  preview: boolean
  items: ScriptAIBatchItem[]
  warnings?: string[]
  import_draft_id?: number
  import_applied?: number
}

export function batchScriptAI(
  publicId: string,
  scriptId: number,
  body: {
    task: 'series_outline' | 'season_outlines' | 'episode_scripts' | 'full_series'
    season_no?: number
    episode_no?: number
    episode_nos?: number[]
    prompt?: string
    model_id?: string
    preview?: boolean
    force_overwrite_draft?: boolean
  },
): Promise<ScriptAIBatchResult> {
  return apiRequest<ScriptAIBatchResult>(scriptDocPath(publicId, scriptId, '/ai/batch'), {
    method: 'POST',
    body,
  })
}

export function applyScriptAI(
  publicId: string,
  scriptId: number,
  episodeNo: number,
  body: {
    prompt?: string
    model_id?: string
    text?: string
    selection_start?: number
    selection_end?: number
    scene_no?: number
    preview?: boolean
  },
): Promise<{ text: string; applied: boolean; cost?: number; script?: Script }> {
  return apiRequest(`${scriptEpisodePath(publicId, scriptId, episodeNo)}/ai/apply`, {
    method: 'POST',
    body,
  })
}

export function suggestScriptAI(
  publicId: string,
  scriptId: number,
  episodeNo: number,
  body: { prompt: string; model_id?: string; context?: string; scene_no?: number },
): Promise<{ text: string; applied: boolean; cost?: number }> {
  return apiRequest(`${scriptEpisodePath(publicId, scriptId, episodeNo)}/ai/suggest`, {
    method: 'POST',
    body,
  })
}

export async function downloadScriptExport(
  productionId: string,
  scriptId: number,
  episodeNo: number,
  format: 'fountain' | 'pdf' | 'fdx' = 'fountain',
  convention?: 'cn' | 'hollywood',
): Promise<void> {
  await ensureFreshAccessToken()
  const params = new URLSearchParams()
  params.set('format', format)
  if (convention) params.set('convention', convention)
  const res = await fetch(
    `${SILKREELS_API_BASE}${scriptEpisodePath(productionId, scriptId, episodeNo)}/export?${params}`,
    {
      headers: {
        Authorization: `Bearer ${readAccessToken()}`,
        [TENANT_HEADER]: getActiveTenantCode(),
        'Accept-Language': resolveApiLocale(),
      },
      credentials: 'include',
    },
  )
  if (!res.ok) throw new Error('export failed')
  const blob = await res.blob()
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const match = /filename=([^;]+)/i.exec(disposition)
  const filename = match?.[1]?.trim() ?? (format === 'pdf' ? 'script.pdf' : format === 'fdx' ? 'script.fdx' : 'script.fountain')
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export function breakdownScript(
  publicId: string,
  scriptId: number,
  episodeNo = 1,
): Promise<ScriptBreakdownResult> {
  return apiRequest<ScriptBreakdownResult>(
    `${scriptEpisodePath(publicId, scriptId, episodeNo)}/breakdown`,
    { method: 'POST' },
  )
}

export function ensureShotDrafts(
  publicId: string,
  scriptId: number,
  episodeNo = 1,
): Promise<ScriptBreakdownResult> {
  return apiRequest<ScriptBreakdownResult>(
    `${scriptEpisodePath(publicId, scriptId, episodeNo)}/ensure-shot-drafts`,
    { method: 'POST' },
  )
}

export function listShots(
  publicId: string,
  opts?: { episode_no?: number; script_id?: number; limit?: number; offset?: number },
): Promise<ShotList> {
  const params = new URLSearchParams()
  if (opts?.episode_no != null) params.set('episode_no', String(opts.episode_no))
  if (opts?.script_id != null) params.set('script_id', String(opts.script_id))
  if (opts?.limit != null) params.set('limit', String(opts.limit))
  if (opts?.offset != null) params.set('offset', String(opts.offset))
  const q = params.toString()
  return apiRequest<ShotList & { items?: Shot[] | null }>(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/shots${q ? `?${q}` : ''}`,
  ).then((res) => ({
    items: res.items ?? [],
    total: res.total ?? (res.items ?? []).length,
    limit: res.limit ?? opts?.limit ?? 20,
    offset: res.offset ?? opts?.offset ?? 0,
  }))
}

export function createShot(
  publicId: string,
  body: {
    script_id?: number
    episode_no?: number
    scene_no?: number
    description?: string
    duration_sec?: number
  },
): Promise<Shot> {
  return apiRequest<Shot>(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/shots`, {
    method: 'POST',
    body,
  })
}

export function getShot(productionId: string, shotId: string): Promise<Shot> {
  return apiRequest<Shot>(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/shots/${encodeURIComponent(shotId)}`,
  )
}

export function listTakes(productionId: string, shotId: string): Promise<ShotTakeList> {
  return apiRequest<ShotTakeList>(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/shots/${encodeURIComponent(shotId)}/takes`,
  )
}

export function createTake(
  productionId: string,
  shotId: string,
  body: {
    prompt?: string
    model_id?: string
    ai_task_id?: string
    media_url?: string
    media_kind?: string
    status?: string
  },
): Promise<ShotTake> {
  return apiRequest<ShotTake>(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/shots/${encodeURIComponent(shotId)}/takes`,
    { method: 'POST', body },
  )
}

export function patchTake(
  productionId: string,
  shotId: string,
  takeId: number,
  body: Record<string, unknown>,
): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/shots/${encodeURIComponent(shotId)}/takes/${takeId}`,
    { method: 'PATCH', body },
  )
}

export function preferTake(
  productionId: string,
  shotId: string,
  takeId: number,
): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/shots/${encodeURIComponent(shotId)}/takes/prefer`,
    { method: 'POST', body: { take_id: takeId } },
  )
}

export interface BibleCharacter {
  public_id: string
  name: string
  description: string
  reference_url: string
  reference_urls?: string[]
  prompt_hint: string
  sort_order: number
  updated_at?: string
}

export interface BibleLocation {
  public_id: string
  name: string
  description: string
  reference_url: string
  reference_urls?: string[]
  prompt_hint: string
  sort_order: number
  updated_at?: string
}

export interface BibleProp {
  public_id: string
  name: string
  description: string
  reference_url: string
  reference_urls?: string[]
  prompt_hint: string
  sort_order: number
  updated_at?: string
}

export interface BibleCostume {
  public_id: string
  name: string
  description: string
  reference_url: string
  reference_urls?: string[]
  prompt_hint: string
  sort_order: number
  updated_at?: string
}

export interface BibleStyle {
  art_direction: string
  color_palette: string
  style_prompt: string
  negative_prompt: string
  reference_urls?: string[]
  updated_at: string
}

export interface StoryboardFrame extends Shot {
  preview_url: string
}

export interface BibleListResult<T> {
  items: T[]
  total: number
  limit: number
  offset: number
}

export type BibleListParams = { limit?: number; offset?: number; q?: string }

function bibleListQuery(params?: BibleListParams): string {
  const qs = new URLSearchParams()
  if (params?.limit != null) qs.set('limit', String(params.limit))
  if (params?.offset != null) qs.set('offset', String(params.offset))
  if (params?.q?.trim()) qs.set('q', params.q.trim())
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export function listBibleCharacters(
  productionId: string,
  params?: BibleListParams,
): Promise<BibleListResult<BibleCharacter>> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/characters${bibleListQuery(params)}`,
  )
}

export function createBibleCharacter(
  productionId: string,
  body: {
    name: string
    description?: string
    reference_url?: string
    reference_urls?: string[]
    prompt_hint?: string
  },
): Promise<BibleCharacter> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/characters`, {
    method: 'POST',
    body,
  })
}

export function patchBibleCharacter(
  productionId: string,
  charId: string,
  body: Partial<
    Pick<BibleCharacter, 'name' | 'description' | 'reference_url' | 'prompt_hint' | 'sort_order'> & {
      reference_urls?: string[]
    }
  >,
): Promise<BibleCharacter> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/characters/${encodeURIComponent(charId)}`,
    { method: 'PATCH', body },
  )
}

export function deleteBibleCharacter(productionId: string, charId: string): Promise<{ ok: boolean }> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/characters/${encodeURIComponent(charId)}`,
    { method: 'DELETE' },
  )
}

export function listBibleLocations(
  productionId: string,
  params?: BibleListParams,
): Promise<BibleListResult<BibleLocation>> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/locations${bibleListQuery(params)}`,
  )
}

export function createBibleLocation(
  productionId: string,
  body: {
    name: string
    description?: string
    reference_url?: string
    reference_urls?: string[]
    prompt_hint?: string
  },
): Promise<BibleLocation> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/locations`, {
    method: 'POST',
    body,
  })
}

export function patchBibleLocation(
  productionId: string,
  locId: string,
  body: Partial<
    Pick<BibleLocation, 'name' | 'description' | 'reference_url' | 'prompt_hint' | 'sort_order'> & {
      reference_urls?: string[]
    }
  >,
): Promise<BibleLocation> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/locations/${encodeURIComponent(locId)}`,
    { method: 'PATCH', body },
  )
}

export function deleteBibleLocation(productionId: string, locId: string): Promise<{ ok: boolean }> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/locations/${encodeURIComponent(locId)}`,
    { method: 'DELETE' },
  )
}

export function listBibleProps(
  productionId: string,
  params?: BibleListParams,
): Promise<BibleListResult<BibleProp>> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/props${bibleListQuery(params)}`)
}

export function createBibleProp(
  productionId: string,
  body: {
    name: string
    description?: string
    reference_url?: string
    reference_urls?: string[]
    prompt_hint?: string
  },
): Promise<BibleProp> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/props`, {
    method: 'POST',
    body,
  })
}

export function patchBibleProp(
  productionId: string,
  propId: string,
  body: Partial<
    Pick<BibleProp, 'name' | 'description' | 'reference_url' | 'prompt_hint' | 'sort_order'> & {
      reference_urls?: string[]
    }
  >,
): Promise<BibleProp> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/props/${encodeURIComponent(propId)}`,
    { method: 'PATCH', body },
  )
}

export function deleteBibleProp(productionId: string, propId: string): Promise<{ ok: boolean }> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/props/${encodeURIComponent(propId)}`,
    { method: 'DELETE' },
  )
}

export function listBibleCostumes(
  productionId: string,
  params?: BibleListParams,
): Promise<BibleListResult<BibleCostume>> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/costumes${bibleListQuery(params)}`,
  )
}

export function createBibleCostume(
  productionId: string,
  body: {
    name: string
    description?: string
    reference_url?: string
    reference_urls?: string[]
    prompt_hint?: string
  },
): Promise<BibleCostume> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/costumes`, {
    method: 'POST',
    body,
  })
}

export function patchBibleCostume(
  productionId: string,
  costumeId: string,
  body: Partial<
    Pick<BibleCostume, 'name' | 'description' | 'reference_url' | 'prompt_hint' | 'sort_order'> & {
      reference_urls?: string[]
    }
  >,
): Promise<BibleCostume> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/costumes/${encodeURIComponent(costumeId)}`,
    { method: 'PATCH', body },
  )
}

export function deleteBibleCostume(productionId: string, costumeId: string): Promise<{ ok: boolean }> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/costumes/${encodeURIComponent(costumeId)}`,
    { method: 'DELETE' },
  )
}

export function getBibleStyle(productionId: string): Promise<BibleStyle> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/style`)
}

export function saveBibleStyle(
  productionId: string,
  body: Partial<BibleStyle>,
): Promise<BibleStyle> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(productionId)}/bible/style`, {
    method: 'PUT',
    body,
  })
}

export function listStoryboard(productionId: string, episodeNo?: number): Promise<{ items: StoryboardFrame[] }> {
  const q = episodeNo ? `?episode_no=${episodeNo}` : ''
  return apiRequest<{ items?: StoryboardFrame[] | null }>(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/storyboard${q}`,
  ).then((res) => ({ items: res.items ?? [] }))
}

export async function downloadShotsCsv(productionId: string, episodeNo?: number): Promise<void> {
  await ensureFreshAccessToken()
  const q = episodeNo ? `?episode_no=${episodeNo}` : ''
  const res = await fetch(
    `${SILKREELS_API_BASE}/api/v1/studio/productions/${encodeURIComponent(productionId)}/shots/export.csv${q}`,
    {
      headers: {
        Authorization: `Bearer ${readAccessToken()}`,
        [TENANT_HEADER]: getActiveTenantCode(),
        'Accept-Language': resolveApiLocale(),
      },
      credentials: 'include',
    },
  )
  if (!res.ok) throw new Error('export failed')
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'shot-list.csv'
  a.click()
  URL.revokeObjectURL(url)
}

export function patchShot(
  productionId: string,
  shotId: string,
  body: {
    description?: string
    shot_size?: string
    camera_angle?: string
    camera_movement?: string
    dialogue?: string
    sound_note?: string
    music_note?: string
    storyboard_note?: string
    status?: string
    duration_sec?: number
  },
): Promise<Shot> {
  return apiRequest<Shot>(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/shots/${encodeURIComponent(shotId)}`,
    { method: 'PATCH', body },
  )
}

export function deleteShot(productionId: string, shotId: string): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/shots/${encodeURIComponent(shotId)}`,
    { method: 'DELETE' },
  )
}

export interface ReviewComment {
  public_id: string
  shot_public_id?: string
  asset_public_id?: string
  author_id: number
  body: string
  time_start_ms?: number | null
  time_end_ms?: number | null
  fps_snapshot?: number | null
  frame_start?: number | null
  frame_end?: number | null
  resolved: boolean
  resolved_at?: string | null
  created_at: string
}

export type CreateReviewCommentPayload = {
  body: string
  time_start_ms?: number
  time_end_ms?: number
  fps_snapshot?: number
}

/** @deprecated use CreateReviewCommentPayload */
export type CreateShotCommentPayload = CreateReviewCommentPayload

export interface ReviewQueueItem {
  item_type: 'shot' | 'asset'
  public_id: string
  title: string
  description?: string
  subject_type?: 'shot' | 'character' | 'location' | 'prop' | 'costume' | 'style'
  asset_kind?: string
  note?: string
  status: string
  open_comments: number
  preview_url?: string
  preview_kind?: string
  shot_public_id?: string
  bible_character_public_id?: string
  bible_location_public_id?: string
  /** @deprecated use title — kept for shot rows */
  slug?: string
  script_id?: number
  episode_no?: number
  scene_no?: number
  shot_no?: number
  shot_size?: string
  duration_sec?: number
}

export type ReviewSubjectFilter = 'all' | 'shot' | 'character' | 'location' | 'prop' | 'costume' | 'style'

export interface ActivityItem {
  id: number
  actor_id: number
  action: string
  summary: string
  ref_type: string
  ref_public_id: string
  created_at: string
}

export function listReviewQueue(productionId: string): Promise<{ items: ReviewQueueItem[] }> {
  return apiRequest<{ items?: ReviewQueueItem[] | null }>(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/reviews/queue`,
  ).then((res) => ({ items: normalizeListItems(res.items) }))
}

export function listShotComments(
  productionId: string,
  shotId: string,
): Promise<{ items: ReviewComment[] }> {
  return apiRequest<{ items?: ReviewComment[] | null }>(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/shots/${encodeURIComponent(shotId)}/comments`,
  ).then((res) => ({ items: normalizeListItems(res.items) }))
}

export function createShotComment(
  productionId: string,
  shotId: string,
  body: string,
  timing?: Pick<CreateShotCommentPayload, 'time_start_ms' | 'time_end_ms' | 'fps_snapshot'>,
): Promise<ReviewComment> {
  const payload: CreateShotCommentPayload = { body }
  if (timing?.time_start_ms != null && timing?.time_end_ms != null) {
    payload.time_start_ms = timing.time_start_ms
    payload.time_end_ms = timing.time_end_ms
    if (timing.fps_snapshot != null) payload.fps_snapshot = timing.fps_snapshot
  }
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/shots/${encodeURIComponent(shotId)}/comments`,
    { method: 'POST', body: payload },
  )
}

export function resolveComment(
  productionId: string,
  shotId: string,
  commentId: string,
  resolved: boolean,
): Promise<{ ok: boolean }> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/shots/${encodeURIComponent(shotId)}/comments/${encodeURIComponent(commentId)}`,
    { method: 'PATCH', body: { resolved } },
  )
}

export function listAssetComments(
  productionId: string,
  assetId: string,
): Promise<{ items: ReviewComment[] }> {
  return apiRequest<{ items?: ReviewComment[] | null }>(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/assets/${encodeURIComponent(assetId)}/comments`,
  ).then((res) => ({ items: normalizeListItems(res.items) }))
}

export function createAssetComment(
  productionId: string,
  assetId: string,
  body: string,
  timing?: Pick<CreateReviewCommentPayload, 'time_start_ms' | 'time_end_ms' | 'fps_snapshot'>,
): Promise<ReviewComment> {
  const payload: CreateReviewCommentPayload = { body }
  if (timing?.time_start_ms != null && timing?.time_end_ms != null) {
    payload.time_start_ms = timing.time_start_ms
    payload.time_end_ms = timing.time_end_ms
    if (timing.fps_snapshot != null) payload.fps_snapshot = timing.fps_snapshot
  }
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/assets/${encodeURIComponent(assetId)}/comments`,
    { method: 'POST', body: payload },
  )
}

export function resolveAssetComment(
  productionId: string,
  assetId: string,
  commentId: string,
  resolved: boolean,
): Promise<{ ok: boolean }> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/assets/${encodeURIComponent(assetId)}/comments/${encodeURIComponent(commentId)}`,
    { method: 'PATCH', body: { resolved } },
  )
}

export function submitShotReview(
  productionId: string,
  shotId: string,
  note?: string,
): Promise<Shot> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/shots/${encodeURIComponent(shotId)}/submit-review`,
    { method: 'POST', body: note ? { note } : {} },
  )
}

export function submitAssetReview(
  productionId: string,
  assetId: string,
  note?: string,
): Promise<ProductionAsset> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/assets/${encodeURIComponent(assetId)}/submit-review`,
    { method: 'POST', body: note ? { note } : {} },
  )
}

export function approveAsset(productionId: string, assetId: string): Promise<ProductionAsset> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/assets/${encodeURIComponent(assetId)}/approve`,
    { method: 'POST', body: {} },
  )
}

export function requestAssetChanges(
  productionId: string,
  assetId: string,
  feedback: string,
): Promise<ProductionAsset> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/assets/${encodeURIComponent(assetId)}/request-changes`,
    { method: 'POST', body: { feedback } },
  )
}

export function approveShot(productionId: string, shotId: string): Promise<Shot> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/shots/${encodeURIComponent(shotId)}/approve`,
    { method: 'POST', body: {} },
  )
}

export function requestShotChanges(
  productionId: string,
  shotId: string,
  feedback: string,
): Promise<Shot> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/shots/${encodeURIComponent(shotId)}/request-changes`,
    { method: 'POST', body: { feedback } },
  )
}

export function listActivity(
  productionId: string,
  opts?: { limit?: number; offset?: number },
): Promise<{ items: ActivityItem[]; total: number; limit: number; offset: number }> {
  const limit = opts?.limit ?? 10
  const offset = opts?.offset ?? 0
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/activity?limit=${limit}&offset=${offset}`,
  )
}

export interface TimelineClip extends Shot {
  media_url?: string
  media_kind?: string
  sort_order: number
}

export interface Timeline {
  episode_no: number
  aspect_ratio: string
  total_duration_sec: number
  approved_clip_count: number
  items: TimelineClip[]
}

export interface EpisodeExport {
  public_id: string
  episode_no: number
  status: string
  manifest: Record<string, unknown>
  output_url?: string
  error_message?: string
  created_at: string
}

export interface PublishToWorksResult {
  work_id: number
  work_public_id: string
  work_status: string
  episodes_synced: number
}

export function listTimeline(productionId: string, episodeNo = 1): Promise<Timeline> {
  return apiRequest<Timeline & { items?: TimelineClip[] | null }>(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/timeline?episode_no=${episodeNo}`,
  ).then((res) => ({
    ...res,
    aspect_ratio: res.aspect_ratio || '',
    total_duration_sec: res.total_duration_sec ?? 0,
    approved_clip_count: res.approved_clip_count ?? 0,
    items: normalizeListItems(res.items),
  }))
}

export function reorderTimeline(
  productionId: string,
  episodeNo: number,
  shotPublicIds: string[],
): Promise<{ ok: boolean }> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(productionId)}/timeline/reorder`, {
    method: 'PUT',
    body: { episode_no: episodeNo, shot_public_ids: shotPublicIds },
  })
}

export function createEpisodeExport(productionId: string, episodeNo: number): Promise<EpisodeExport> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(productionId)}/timeline/export`, {
    method: 'POST',
    body: { episode_no: episodeNo },
  })
}

export function listEpisodeExports(
  productionId: string,
  episodeNo?: number,
): Promise<{ items: EpisodeExport[] }> {
  const q = episodeNo ? `?episode_no=${episodeNo}` : ''
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(productionId)}/timeline/exports${q}`,
  )
}

export function publishToWorks(
  productionId: string,
  body: { publish?: boolean; visibility?: string } = {},
): Promise<PublishToWorksResult> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(productionId)}/publish-to-works`, {
    method: 'POST',
    body,
  })
}

export function downloadExportManifest(exp: EpisodeExport): void {
  const blob = new Blob([JSON.stringify(exp.manifest, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `episode-${exp.episode_no}-manifest.json`
  a.click()
  URL.revokeObjectURL(url)
}

// --- 项目预算 + 派工单（000263 · 1A+2b+3b）---

export interface ProductionBudgetView {
  pool_balance: number
  total_funded: number
  total_allocated: number
  departments: Array<{
    department_id: string
    department_name: string
    sub_pool_balance: number
    total_allocated: number
  }>
}

export interface ProductionAssignmentItem {
  item_type: string
  item_public_id: string
}

export interface ProductionAssignment {
  public_id: string
  assignee_id: number
  created_by: number
  department_id?: string
  department_name?: string
  quota_allocated: number
  quota_consumed: number
  quota_remaining: number
  status: string
  items: ProductionAssignmentItem[]
  submitted_at?: string
  reviewed_at?: string
  review_note?: string
  created_at: string
  updated_at: string
}

function normalizeProductionBudget(view: ProductionBudgetView): ProductionBudgetView {
  return { ...view, departments: view.departments ?? [] }
}

export function getProductionBudget(publicId: string): Promise<ProductionBudgetView> {
  return apiRequest<ProductionBudgetView>(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/budget`,
  ).then(normalizeProductionBudget)
}

export function fundProductionBudget(
  publicId: string,
  body: { amount: number; note?: string },
): Promise<ProductionBudgetView> {
  return apiRequest<ProductionBudgetView>(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/budget/fund`, {
    method: 'POST',
    body,
  }).then(normalizeProductionBudget)
}

export function allocateDepartmentBudget(
  publicId: string,
  body: { department_id: string; amount: number; note?: string },
): Promise<ProductionBudgetView> {
  return apiRequest<ProductionBudgetView>(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/budget/allocate-department`,
    {
      method: 'POST',
      body,
    },
  ).then(normalizeProductionBudget)
}

export function listProductionAssignments(
  publicId: string,
  status?: string,
  opts?: { limit?: number; offset?: number },
): Promise<{ items: ProductionAssignment[]; total: number; limit: number; offset: number }> {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  if (opts?.limit != null) params.set('limit', String(opts.limit))
  if (opts?.offset != null) params.set('offset', String(opts.offset))
  const q = params.toString()
  return apiRequest<{
    items?: ProductionAssignment[] | null
    total?: number
    limit?: number
    offset?: number
  }>(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/production-assignments${q ? `?${q}` : ''}`,
  ).then((res) => ({
    items: (res.items ?? []).map((a) => ({ ...a, items: a.items ?? [] })),
    total: res.total ?? (res.items ?? []).length,
    limit: res.limit ?? opts?.limit ?? 20,
    offset: res.offset ?? opts?.offset ?? 0,
  }))
}

export function createProductionAssignments(
  publicId: string,
  body: {
    department_id?: string
    assignments: Array<{
      assignee_id: number
      quota: number
      items: ProductionAssignmentItem[]
    }>
  },
): Promise<{ items: ProductionAssignment[] }> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/production-assignments`, {
    method: 'POST',
    body,
  })
}

export function getProductionAssignmentScopes(
  publicId: string,
): Promise<{ items: Array<{ item_type: string; item_public_id: string; editable: boolean }> }> {
  return apiRequest(`/api/v1/studio/productions/${encodeURIComponent(publicId)}/production-assignments/scopes`)
}

export function reclaimDepartmentBudget(
  publicId: string,
  body: { department_id: string; amount: number; note?: string },
): Promise<ProductionBudgetView> {
  return apiRequest<ProductionBudgetView>(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/budget/reclaim-department`,
    {
      method: 'POST',
      body,
    },
  ).then(normalizeProductionBudget)
}

export function startProductionAssignment(
  publicId: string,
  assignmentId: string,
): Promise<ProductionAssignment> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/production-assignments/${encodeURIComponent(assignmentId)}/start`,
    { method: 'POST' },
  )
}

export function submitProductionAssignment(
  publicId: string,
  assignmentId: string,
): Promise<ProductionAssignment> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/production-assignments/${encodeURIComponent(assignmentId)}/submit`,
    { method: 'POST' },
  )
}

export function approveProductionAssignment(
  publicId: string,
  assignmentId: string,
  body: { note?: string } = {},
): Promise<ProductionAssignment> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/production-assignments/${encodeURIComponent(assignmentId)}/approve`,
    { method: 'POST', body },
  )
}

export function requestProductionAssignmentChanges(
  publicId: string,
  assignmentId: string,
  body: { note?: string } = {},
): Promise<ProductionAssignment> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/production-assignments/${encodeURIComponent(assignmentId)}/request-changes`,
    { method: 'POST', body },
  )
}

export function topUpProductionAssignment(
  publicId: string,
  assignmentId: string,
  body: { quota: number; note?: string },
): Promise<ProductionAssignment> {
  return apiRequest(
    `/api/v1/studio/productions/${encodeURIComponent(publicId)}/production-assignments/${encodeURIComponent(assignmentId)}/top-up`,
    { method: 'POST', body },
  )
}
